import { useCallback } from "react";
import { notification } from "antd";
import localforage from "localforage";

export function useQueueLogic(API_URL, state) {
  const {
    setQueueStatus,
    setBusinessMessage,
    setCurrentNumber,
    setBusinessName,
    setUserId,
    setUserAddress,
    setCurrentLocation,
    setShowUserStatus,
    profileForm,
    setLoading,
    queueStatus,
    currentNumber,
    timeoutRef
  } = state;

  const fetchQueueInfo = useCallback(
    async (token) => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/getQueueInfo`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!data.error) {
          setQueueStatus(data.isActive);
          setBusinessMessage(data.businessMessage);
          setCurrentNumber(data.queueNumber);
          setBusinessName(data.businessName);
          setUserId(data.userId);
          setUserAddress(data.address);
          setCurrentLocation(data.locationDetails);
          setShowUserStatus(!data.isVerified);
          profileForm.setFieldsValue({
            businessName: data.businessName,
            mobileNumber: data.mobileNumber,
            email: data.email,
            currentAddress: data.location?.address,
          });
          if (data.token) {
            await localforage.setItem("access_token", data.token);
          }
        }
      } catch (error) {
        console.error("Error fetching queue info:", error);
      } finally {
        setLoading(false);
      }
    },
    [API_URL, profileForm, setQueueStatus, setBusinessMessage, setCurrentNumber, setBusinessName, setUserId, setUserAddress, setCurrentLocation, setShowUserStatus, setLoading]
  );

  const updateQueueInfo = useCallback(
    async (value) => {
      const token = await localforage.getItem("access_token");
      if (!token) {
        notification.error({ message: "User not logged in" });
        return;
      }

      try {
        const response = await fetch(`${API_URL}/updateQueueInfo`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(value),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        }
      } catch (error) {
        notification.error({ message: "Error updating queue info" });
        console.error("Error updating queue info:", error);
      }
    },
    [API_URL]
  );

  const setBusinessMessageForUsers = useCallback(
    async (value, setText) => {
      const token = await localforage.getItem("access_token");
      if (!token) {
        notification.error({ message: "User not logged in" });
        return;
      }

      const currentDate = new Date();
      const formattedDateTime = currentDate.toLocaleString("en-US", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const businessMessageToUpdate = {
        message: value,
        time: formattedDateTime,
      };

      setBusinessMessage(businessMessageToUpdate);
      setText("");

      try {
        const response = await fetch(`${API_URL}/setBusinessMessageForUsers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ businessMessage: businessMessageToUpdate }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        }
      } catch (error) {
        notification.error({ message: "Error sending message" });
        console.error("Error sending message:", error);
      }
    },
    [API_URL, setBusinessMessage]
  );

  const throttle = useCallback((func, limit = 500) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      func();
    }, limit);
  }, [timeoutRef]);

  const handleIncrement = useCallback(async () => {
    if (queueStatus === false) {
      notification.warning({ message: "please set status as active" });
      throw new Error("Queue is inactive");
    }

    const newNumber = currentNumber + 1;
    setCurrentNumber(newNumber);

    try {
      await updateQueueInfo({ queueNumber: newNumber });
    } catch (error) {
      console.error("Error in increment:", error);
      notification.error({ message: "Error incrementing queue number" });
      throw error;
    }
  }, [queueStatus, currentNumber, updateQueueInfo, setCurrentNumber]);

  const handleDecrement = useCallback(async () => {
    if (queueStatus === false) {
      notification.warning({ message: "please set status as active" });
      throw new Error("Queue is inactive");
    }
    if (currentNumber <= 0) {
      throw new Error("Queue number cannot be negative");
    }

    const newNumber = currentNumber - 1;
    setCurrentNumber(newNumber);

    try {
      await updateQueueInfo({ queueNumber: newNumber });
    } catch (error) {
      console.error("Error in decrement:", error);
      notification.error({ message: "Error decrementing queue number" });
      throw error;
    }
  }, [queueStatus, currentNumber, updateQueueInfo, setCurrentNumber]);

  const handleReset = useCallback(async () => {
    setCurrentNumber(0);

    try {
      await updateQueueInfo({ queueNumber: 0 });
    } catch (error) {
      console.error("Error in reset:", error);
      notification.error({ message: "Error resetting queue" });
      throw error;
    }
  }, [updateQueueInfo, setCurrentNumber]);

  const toggleActiveStatus = useCallback(() => {
    setQueueStatus((prev) => {
      throttle(() => updateQueueInfo({ isActive: !prev }));
      return !prev;
    });
  }, [throttle, updateQueueInfo, setQueueStatus]);

  const setNumber = useCallback(
    async (newNumber) => {
      if (isNaN(newNumber)) {
        throw new Error("Invalid number");
      }

      setCurrentNumber(newNumber);

      try {
        await updateQueueInfo({ queueNumber: newNumber });
      } catch (error) {
        console.error("Error in number change:", error);
        notification.error({ message: "Error updating queue number" });
        throw error;
      }
    },
    [updateQueueInfo, setCurrentNumber]
  );

  return { fetchQueueInfo, updateQueueInfo, setBusinessMessageForUsers, handleIncrement, handleDecrement, handleReset, toggleActiveStatus, setNumber };
}
